'use client';

import * as React from 'react';
import { useEffect } from 'react';
import { Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import GlobalStyles from '@mui/material/GlobalStyles';
import * as Sentry from '@sentry/react';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';

import { useMediaQuery } from '@/hooks/v2/use-media-query';
import { useEchoPrivateEvent } from '@/hooks/v2/use-echo';
import { useSettings } from '@/hooks/v2/use-settings';
import { useUser } from '@/hooks/v2/use-user';
import { Logo } from '@/components/core/v2/logo';
import ErrorBoundaryFallback from '@/components/error-boundary-fallback';
import { ProductTourProvider } from '@/components/tour/product-tour-provider';

import { layoutConfig } from '../config';
import { MainNav } from './main-nav';
import { SideNav } from './side-nav';

export interface VerticalLayoutProps {
  children?: React.ReactNode;
}

export function VerticalLayout({ children }: VerticalLayoutProps): React.JSX.Element {
  const { user, account, isLoading, community } = useUser();
  console.log('account', account);
  const isMobile = useMediaQuery('down', 'lg');
  const theme = useTheme();
  useEchoPrivateEvent(
    user?.id ? `user.${user.id}` : '',
    `.global-announcement.${community?.id ?? ''}`,
    (data: any) => {
      console.log('[VerticalLayout] global-announcement received:', data);
      toast.info('Test event received', { position: 'top-right' });
    },
    [user?.id, community?.id]
  );

  if (isLoading) {
    return (
      <Fade in={isLoading} timeout={1000}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Logo />
        </Box>
      </Fade>
    );
  }

  return (
    <ProductTourProvider>
      <GlobalStyles
        styles={{
          body: {
            '--MainNav-height': '56px',
            '--MainNav-zIndex': 1000,
            '--SideNav-width': '280px',
            '--SideNav-zIndex': 1100,
            '--MobileNav-width': '320px',
            '--MobileNav-zIndex': 1100,
          },
        }}
      />
      <Box
        sx={{
          bgcolor: 'var(--mui-palette-background-default)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: '100%',
        }}
      >
        <SideNav
          account={account || undefined}
          bottomItems={layoutConfig.bottomNavItems}
          color="discrete"
          community={community || undefined}
          items={layoutConfig.navItems}
          user={user || undefined}
        />
        <Box sx={{ display: 'flex', flex: '1 1 auto', flexDirection: 'column', pl: { lg: 'var(--SideNav-width)' } }}>
          <MainNav bottomItems={layoutConfig.bottomNavItems} items={layoutConfig.navItems} />
          <Box
            component="main"
            sx={{
              '--Content-margin': '0 auto',
              '--Content-maxWidth': 'var(--maxWidth-xl)',
              '--Content-paddingX': '24px',
              '--Content-paddingY': { xs: '24px', lg: '56px' },
              '--Content-padding': 'var(--Content-paddingY) var(--Content-paddingX)',
              '--Content-width': '100%',
              display: 'flex',
              flex: '1 1 auto',
              flexDirection: 'column',
            }}
          >
            <Sentry.ErrorBoundary
              fallback={({ error, resetError }) => (
                <ErrorBoundaryFallback error={error as Error} resetErrorBoundary={resetError} />
              )}
              onError={(error) => {
                if (window.newrelic?.noticeError) {
                  window.newrelic.noticeError(error);
                }
              }}
            >
              {children}
            </Sentry.ErrorBoundary>
          </Box>
        </Box>
      </Box>
    </ProductTourProvider>
  );
}
